import {Image} from '@primer/react-brand/esm'
import {getMediaSizeVars} from '../../_utils/media-size-vars'
import {DESKTOP_MEDIA_QUERY, type ResponsiveImages} from './pick-responsive-images'

import styles from './ResponsiveImage.module.css'

type Props = {
  images: ResponsiveImages
  className?: string
  loading?: 'lazy' | 'eager'
}

/**
 * Renders a Contentful image, or a `<picture>` that lets the browser choose the
 * desktop or default image by screen size, so the right file is loaded on the first render.
 * Image resizes to 100%, make sure to set the container's width appropriately.
 *
 * In a <picture>, the <img> takes its width and height attributes from the default (narrowest)
 * image. To let CSS size it correctly for the desktop image too, the sizes are also exposed as
 * unitless custom properties (`--width`, `--height`, `--desktop-width`, `--desktop-height`), see
 * `getMediaSizeVars`. A single image doesn't need them, its width and height attributes are enough.
 */

export function ResponsiveImage({images, className, loading = 'lazy'}: Props) {
  const {fallback, desktop} = images
  const {file, description: assetDescription} = fallback.fields.asset.fields
  const alt = fallback.fields.description ?? assetDescription ?? ''
  const width = fallback.fields.width ?? file.details?.image?.width
  const height = fallback.fields.height ?? file.details?.image?.height
  const desktopUrl = desktop?.fields.asset.fields.file.url
  return desktopUrl ? (
    <Image
      as="picture"
      className={`${styles.Picture} ${className ?? ''}`}
      src={file.url}
      sources={[{srcset: desktopUrl, media: DESKTOP_MEDIA_QUERY}]}
      width={width}
      height={height}
      alt={alt}
      loading={loading}
      style={getMediaSizeVars(fallback, desktop)}
    />
  ) : (
    <Image
      className={`${styles.Image} ${className ?? ''}`}
      src={file.url}
      width={width}
      height={height}
      alt={alt}
      loading={loading}
    />
  )
}
